import React, { useEffect, useMemo, useState } from "react";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, User, Phone, Wallet, Shield, Clock, StickyNote, CalendarDays, Plus, Trash2, Edit3, LayoutGrid, List } from "lucide-react";

function pad(n) { return String(n).padStart(2, "0"); }
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

const STATUSES = ["empty", "rented", "reserved"];
const STATUS_CLASS = {
  empty: "cal-empty",
  rented: "cal-rented",
  reserved: "cal-reserved",
};

const emptyForm = {
  status: "rented",
  guest_name: "",
  guest_phone: "",
  amount: "",
  deposit: "",
  insurance: "",
  hours: "",
  start_time: "",
  end_time: "",
  notes: "",
};

export default function CalendarPage() {
  const { t, lang } = useLang();
  const { format: money, code: ccy } = useCurrency();
  const [cursor, setCursor] = useState(() => new Date());
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [bookingsByDate, setBookingsByDate] = useState({}); // { "YYYY-MM-DD": [booking...] }
  const [selectedDay, setSelectedDay] = useState(null);
  const [editingId, setEditingId] = useState(null); // booking id being edited (null = new)
  const [form, setForm] = useState(emptyForm);

  const selectedProperty = properties.find((p) => p.id === propertyId);
  const isHourly = selectedProperty?.type === "sports_field";

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProperties(r.data);
      if (r.data[0]) setPropertyId(r.data[0].id);
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);

  const loadMonth = () => {
    if (!propertyId) return;
    const start = ymd(monthStart);
    const end = ymd(monthEnd);
    api.get(`/bookings`, { params: { property_id: propertyId, start, end } })
      .then((r) => {
        const map = {};
        r.data.forEach((b) => {
          if (!map[b.date]) map[b.date] = [];
          map[b.date].push(b);
        });
        // sort each day by start_time
        Object.values(map).forEach((list) => list.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")));
        setBookingsByDate(map);
      })
      .catch((e) => toast.error(formatApiError(e)));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadMonth, [propertyId, cursor]);

  const days = useMemo(() => {
    const firstWeekday = monthStart.getDay();
    const total = monthEnd.getDate();
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push({ empty: true });
    for (let d = 1; d <= total; d++) {
      const dt = new Date(cursor.getFullYear(), cursor.getMonth(), d);
      cells.push({ date: dt, key: ymd(dt) });
    }
    while (cells.length % 7 !== 0) cells.push({ empty: true });
    return cells;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor]);

  const openDay = (day) => {
    setSelectedDay(day);
    setEditingId(null);
    // For non-hourly: pre-fill from single existing booking (if any)
    if (!isHourly) {
      const b = (bookingsByDate[day.key] || [])[0];
      setForm({
        status: b?.status || "empty",
        guest_name: b?.guest_name || "",
        guest_phone: b?.guest_phone || "",
        amount: b?.amount ?? "",
        deposit: b?.deposit ?? "",
        insurance: b?.insurance ?? "",
        hours: b?.hours ?? "",
        start_time: b?.start_time || "",
        end_time: b?.end_time || "",
        notes: b?.notes || "",
      });
    } else {
      setForm(emptyForm);
    }
  };

  const cycleStatus = async (day, e) => {
    e.stopPropagation();
    if (isHourly) return; // don't cycle on hourly properties
    const cur = (bookingsByDate[day.key] || [])[0];
    const next = STATUSES[(STATUSES.indexOf(cur?.status || "empty") + 1) % STATUSES.length];
    const payload = {
      status: next,
      guest_name: cur?.guest_name || "",
      guest_phone: cur?.guest_phone || "",
      amount: cur?.amount || 0,
      deposit: cur?.deposit || 0,
      insurance: cur?.insurance || 0,
      hours: cur?.hours || null,
      start_time: cur?.start_time || "",
      end_time: cur?.end_time || "",
      notes: cur?.notes || "",
    };
    await createBooking(day.key, payload);
  };

  const createBooking = async (date, payload) => {
    if (!propertyId) return;
    const body = {
      property_id: propertyId,
      date,
      status: payload.status,
      guest_name: payload.guest_name || "",
      guest_phone: payload.guest_phone || "",
      amount: Number(payload.amount) || 0,
      deposit: Number(payload.deposit) || 0,
      insurance: Number(payload.insurance) || 0,
      hours: payload.hours === "" || payload.hours == null ? null : Number(payload.hours),
      start_time: payload.start_time || "",
      end_time: payload.end_time || "",
      notes: payload.notes || "",
    };
    try {
      await api.post("/bookings", body);
      loadMonth();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateBooking = async (id, payload) => {
    const body = {
      status: payload.status,
      guest_name: payload.guest_name || "",
      guest_phone: payload.guest_phone || "",
      amount: Number(payload.amount) || 0,
      deposit: Number(payload.deposit) || 0,
      insurance: Number(payload.insurance) || 0,
      hours: payload.hours === "" || payload.hours == null ? null : Number(payload.hours),
      start_time: payload.start_time || "",
      end_time: payload.end_time || "",
      notes: payload.notes || "",
    };
    try {
      await api.patch(`/bookings/${id}`, body);
      loadMonth();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteBooking = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      loadMonth();
      toast.success(t("slot_deleted"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleSave = async () => {
    if (!selectedDay) return;
    // Validate amount (required for non-empty status)
    if (form.status !== "empty" || isHourly) {
      const n = Number(form.amount);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error(t("amount_required"));
        return;
      }
    }
    // For hourly bookings, start/end time required
    if (isHourly && (!form.start_time || !form.end_time)) {
      toast.error(t("fields_required"));
      return;
    }
    if (editingId) {
      await updateBooking(editingId, form);
      toast.success(t("slot_saved"));
    } else {
      await createBooking(selectedDay.key, form);
      toast.success(t("saved"));
    }
    if (isHourly) {
      setForm(emptyForm);
      setEditingId(null);
    } else {
      setSelectedDay(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedDay) return;
    // For daily: delete the single booking of that day. For hourly: use per-slot delete.
    const list = bookingsByDate[selectedDay.key] || [];
    if (list[0]) await deleteBooking(list[0].id);
    setSelectedDay(null);
    toast.success(t("deleted"));
  };

  const startEditingSlot = (b) => {
    setEditingId(b.id);
    setForm({
      status: b.status || "rented",
      guest_name: b.guest_name || "",
      guest_phone: b.guest_phone || "",
      amount: b.amount ?? "",
      deposit: b.deposit ?? "",
      insurance: b.insurance ?? "",
      hours: b.hours ?? "",
      start_time: b.start_time || "",
      end_time: b.end_time || "",
      notes: b.notes || "",
    });
  };

  const monthName = new Intl.DateTimeFormat(lang === "ar" ? "ar" : "en", { month: "long", year: "numeric" }).format(cursor);
  const weekdays = [0, 1, 2, 3, 4, 5, 6].map((i) => {
    const d = new Date(2024, 8, 1 + i);
    return new Intl.DateTimeFormat(lang === "ar" ? "ar" : "en", { weekday: "short" }).format(d);
  });

  // Month totals
  const monthTotals = useMemo(() => {
    const all = Object.values(bookingsByDate).flat();
    const income = all.filter((b) => b.status === "rented").reduce((s, b) => s + (Number(b.amount) || 0), 0);
    const deposit = all.reduce((s, b) => s + (Number(b.deposit) || 0), 0);
    const insurance = all.reduce((s, b) => s + (Number(b.insurance) || 0), 0);
    const rented = all.filter((b) => b.status === "rented").length;
    const reserved = all.filter((b) => b.status === "reserved").length;
    return { income, deposit, insurance, rented, reserved };
  }, [bookingsByDate]);

  const dayList = selectedDay ? (bookingsByDate[selectedDay.key] || []) : [];

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
            <CalendarDays className="text-[#2A5948]" size={26} />
            {t("calendar")}
          </h1>
          <p className="text-sm text-[#5C6053] mt-1">{t("click_to_change")}</p>
        </div>
        {properties.length > 1 && (
          <select
            data-testid="calendar-property-select"
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            className="border border-[#E5E1D8] rounded-lg px-3 py-2 text-sm bg-white"
          >
            {properties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Month totals mini strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MiniStat testid="month-income" label={t("income")} value={money(monthTotals.income)} tone="green" />
        <MiniStat testid="month-deposit" label={t("deposit")} value={money(monthTotals.deposit)} tone="brand" />
        <MiniStat testid="month-insurance" label={t("insurance")} value={money(monthTotals.insurance)} tone="amber" />
        <MiniStat testid="month-rented-count" label={t("status_rented")} value={monthTotals.rented} tone="green" />
        <MiniStat testid="month-reserved-count" label={t("status_reserved")} value={monthTotals.reserved} tone="amber" />
      </div>

      <div className="bg-white border border-[#E5E1D8] rounded-2xl p-4 sm:p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <button data-testid="cal-prev-btn" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-2 rounded-lg hover:bg-[#F3EFE7]">
            <ChevronLeft size={18} />
          </button>
          <div className="font-semibold" data-testid="cal-month-label">{monthName}</div>
          <button data-testid="cal-next-btn" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-2 rounded-lg hover:bg-[#F3EFE7]">
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-4 text-xs">
          <LegendItem cls="cal-empty" label={t("status_empty")} />
          <LegendItem cls="cal-rented" label={t("status_rented")} />
          <LegendItem cls="cal-reserved" label={t("status_reserved")} />
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-xs text-[#5C6053] mb-2">
          {weekdays.map((w, i) => <div key={i}>{w}</div>)}
        </div>

        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {days.map((cell, i) => {
            if (cell.empty) return <div key={i} className="aspect-square" />;
            const list = bookingsByDate[cell.key] || [];
            const hasAny = list.length > 0;
            // For hourly: color by presence of any rented
            let status = "empty";
            if (hasAny) {
              if (list.some((b) => b.status === "rented")) status = "rented";
              else if (list.some((b) => b.status === "reserved")) status = "reserved";
            }
            const isToday = ymd(new Date()) === cell.key;
            const totalAmount = list.filter((b) => b.status === "rented").reduce((s, b) => s + (Number(b.amount) || 0), 0);
            return (
              <div
                key={i}
                data-testid={`cal-day-${cell.key}`}
                data-status={status}
                data-count={list.length}
                className={`cal-cell ${STATUS_CLASS[status]} ${isToday ? "ring-2 ring-[#2A5948]" : ""}`}
                onClick={() => openDay(cell)}
                onContextMenu={(e) => { e.preventDefault(); cycleStatus(cell, e); }}
                title={t("click_to_change")}
              >
                <div className="flex flex-col items-center leading-tight">
                  <div className="text-sm sm:text-base font-semibold">{cell.date.getDate()}</div>
                  {isHourly && list.length > 0 && (
                    <div className="text-[10px] opacity-90 mt-0.5">{list.length} × <Clock size={9} className="inline" /></div>
                  )}
                  {!isHourly && list[0]?.amount ? <div className="text-[10px] opacity-90">{list[0].amount}</div> : null}
                  {totalAmount > 0 && isHourly && <div className="text-[10px] opacity-90 font-medium">{totalAmount.toFixed(1)}</div>}
                  {!isHourly && list[0]?.guest_name ? <div className="text-[9px] opacity-80 truncate max-w-full px-1">{list[0].guest_name}</div> : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedDay && (
        <Modal onClose={() => setSelectedDay(null)}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">{t("day_details")}</h3>
            <button data-testid="modal-close-btn" onClick={() => setSelectedDay(null)} className="p-1 rounded hover:bg-[#F3EFE7]"><X size={18} /></button>
          </div>
          <div className="text-sm text-[#5C6053] mt-1" data-testid="modal-date">{selectedDay.key}</div>

          {/* Hourly view: grid + list existing slots + add new form */}
          {isHourly && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm">{t("hourly_grid")}</h4>
                <span className="text-xs text-[#5C6053]">{t("click_hour_to_add")}</span>
              </div>
              <HourGrid
                bookings={dayList}
                onHourClick={(h) => {
                  const hh = pad(h);
                  setEditingId(null);
                  setForm({ ...emptyForm, start_time: `${hh}:00`, end_time: `${pad(h + 1)}:00` });
                }}
                onSlotClick={(b) => startEditingSlot(b)}
                t={t}
                money={money}
              />

              <div className="flex items-center justify-between mb-2 mt-5">
                <h4 className="font-semibold text-sm">{t("field_bookings_day")} ({dayList.length})</h4>
              </div>
              {dayList.length === 0 ? (
                <div className="text-xs text-[#5C6053] py-3 text-center border border-dashed border-[#E5E1D8] rounded-lg">{t("no_bookings_this_day")}</div>
              ) : (
                <div className="space-y-2">
                  {dayList.map((b) => (
                    <div key={b.id} data-testid={`slot-${b.id}`} className={`border rounded-xl p-3 ${b.status === "rented" ? "bg-[#E6F1E7] border-[#2A5948]/30" : "bg-[#FFF3D6] border-[#E5B25D]/40"}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap text-sm">
                            <span className="inline-flex items-center gap-1 font-semibold" dir="ltr">
                              <Clock size={12} />
                              {b.start_time || "?"} - {b.end_time || "?"}
                            </span>
                            {b.guest_name && <span className="text-xs text-[#5C6053]">· {b.guest_name}</span>}
                            {b.guest_phone && <span className="text-xs text-[#5C6053]" dir="ltr">· {b.guest_phone}</span>}
                          </div>
                          <div className="text-xs text-[#5C6053] mt-1">
                            {money(b.amount)}
                            {b.notes && <span> · {b.notes}</span>}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <button data-testid={`edit-slot-${b.id}`} onClick={() => startEditingSlot(b)} className="p-1.5 rounded-lg hover:bg-white/60"><Edit3 size={14} /></button>
                          <button data-testid={`delete-slot-${b.id}`} onClick={() => deleteBooking(b.id)} className="p-1.5 rounded-lg hover:bg-white/60 text-[#C96A52]"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-[#E5E1D8]">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">{editingId ? t("edit") : t("add_time_slot")}</h4>
                  {editingId && (
                    <button onClick={() => { setEditingId(null); setForm(emptyForm); }} className="text-xs text-[#5C6053] hover:text-[#2A5948]">
                      {t("cancel")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Status buttons for non-hourly */}
          {!isHourly && (
            <div className="mt-4 grid grid-cols-3 gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  data-testid={`status-btn-${s}`}
                  onClick={() => setForm({ ...form, status: s })}
                  className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                    form.status === s
                      ? s === "rented" ? "bg-[#3E874B] text-white border-[#3E874B]"
                        : s === "reserved" ? "bg-[#E5B25D] text-[#1C1E17] border-[#E5B25D]"
                        : "bg-white text-[#1C1E17] border-[#2A5948]"
                      : "bg-white border-[#E5E1D8] text-[#5C6053]"
                  }`}
                >
                  {t(`status_${s}`)}
                </button>
              ))}
            </div>
          )}

          {(form.status !== "empty" || isHourly) && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {isHourly && (
                <>
                  <ModalField label={t("from_time")} icon={Clock}>
                    <input data-testid="modal-start-time-input" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="modal-input" />
                  </ModalField>
                  <ModalField label={t("to_time")} icon={Clock}>
                    <input data-testid="modal-end-time-input" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="modal-input" />
                  </ModalField>
                </>
              )}
              <ModalField label={t("guest_name")} icon={User}>
                <input data-testid="modal-guest-input" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className="modal-input" />
              </ModalField>
              <ModalField label={t("guest_phone")} icon={Phone}>
                <input data-testid="modal-guest-phone-input" dir="ltr" value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} className="modal-input" placeholder="+968..." />
              </ModalField>
              <ModalField label={t("amount") + " *"} icon={Wallet}>
                <input data-testid="modal-amount-input" type="number" step="0.01" min="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="modal-input" />
              </ModalField>
              {!isHourly && (
                <>
                  <ModalField label={t("deposit")} icon={Wallet}>
                    <input data-testid="modal-deposit-input" type="number" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} className="modal-input" />
                  </ModalField>
                  <ModalField label={t("insurance")} icon={Shield}>
                    <input data-testid="modal-insurance-input" type="number" value={form.insurance} onChange={(e) => setForm({ ...form, insurance: e.target.value })} className="modal-input" />
                  </ModalField>
                </>
              )}
              <div className="sm:col-span-2">
                <ModalField label={t("notes")} icon={StickyNote}>
                  <textarea data-testid="modal-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="modal-input" />
                </ModalField>
              </div>
            </div>
          )}

          <div className="mt-5 flex items-center justify-end gap-2 flex-wrap">
            <button data-testid="modal-cancel-btn" onClick={() => setSelectedDay(null)} className="px-3 py-2 rounded-lg text-sm border border-[#E5E1D8]">{t("close")}</button>
            {!isHourly && (
              <button data-testid="modal-delete-btn" onClick={handleDelete} className="px-3 py-2 rounded-lg text-sm border border-[#C96A52] text-[#C96A52]">{t("delete")}</button>
            )}
            <button data-testid="modal-save-btn" onClick={handleSave} className="px-4 py-2 rounded-lg text-sm brand-btn inline-flex items-center gap-1">
              {isHourly && !editingId && <Plus size={14} />}
              {t("save")}
            </button>
          </div>

          <style>{`.modal-input { width: 100%; border: 1px solid #E5E1D8; background: #F9F6F0; border-radius: 10px; padding: 8px 12px; font-size: 14px; outline: none; }
            .modal-input:focus { box-shadow: 0 0 0 2px rgba(42,89,72,0.25); border-color: #2A5948; }`}</style>
        </Modal>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone, testid }) {
  const map = {
    green: "bg-[#E6F1E7] text-[#2A5948]",
    brand: "bg-[#2A5948] text-white",
    amber: "bg-[#FFF3D6] text-[#8A6D1F]",
  };
  return (
    <div data-testid={testid} className="rounded-xl border border-[#E5E1D8] bg-white p-3 shadow-sm">
      <div className={`inline-block text-[10px] px-2 py-0.5 rounded ${map[tone]}`}>{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

function LegendItem({ cls, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`w-4 h-4 rounded ${cls} border border-[#E5E1D8]`} />
      <span>{label}</span>
    </div>
  );
}

function ModalField({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <div className="text-xs mb-1 text-[#5C6053] flex items-center gap-1">
        {Icon && <Icon size={12} />}
        {label}
      </div>
      {children}
    </label>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1C1E17]/50 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl p-6 shadow-2xl border border-[#E5E1D8] max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/**
 * HourGrid — Vertical timeline of 24 hours (6:00-24:00 by default). Existing
 * bookings render as colored blocks that span their duration; empty hour cells
 * are clickable to quickly seed a new slot in the add-form.
 */
function HourGrid({ bookings, onHourClick, onSlotClick, t, money }) {
  const START_HOUR = 6;
  const END_HOUR = 24; // exclusive
  const ROW_HEIGHT = 40; // px per hour
  const total = END_HOUR - START_HOUR;

  const parseHM = (s) => {
    if (!s) return null;
    const [h, m] = s.split(":").map((x) => parseInt(x, 10));
    if (Number.isNaN(h)) return null;
    return h + (Number.isFinite(m) ? m / 60 : 0);
  };

  const blocks = bookings
    .map((b) => {
      const s = parseHM(b.start_time);
      const e = parseHM(b.end_time);
      if (s == null || e == null) return null;
      const from = Math.max(s, START_HOUR);
      const to = Math.min(e, END_HOUR);
      if (to <= from) return null;
      return { b, from, to };
    })
    .filter(Boolean);

  return (
    <div className="bg-white border border-[#E5E1D8] rounded-xl overflow-hidden">
      <div className="relative flex" style={{ height: total * ROW_HEIGHT }}>
        {/* Hour labels column */}
        <div className="w-14 flex-shrink-0 border-e border-[#E5E1D8] bg-[#F9F6F0]">
          {Array.from({ length: total }, (_, i) => {
            const h = START_HOUR + i;
            return (
              <div key={h} className="border-b border-[#E5E1D8] text-[10px] text-[#5C6053] px-2 flex items-start pt-1" style={{ height: ROW_HEIGHT }} dir="ltr">
                {String(h).padStart(2, "0")}:00
              </div>
            );
          })}
        </div>

        {/* Timeline area (empty hours + booking blocks overlayed) */}
        <div className="relative flex-1">
          {Array.from({ length: total }, (_, i) => {
            const h = START_HOUR + i;
            return (
              <button
                key={h}
                type="button"
                data-testid={`hour-slot-${h}`}
                onClick={() => onHourClick(h)}
                className="w-full block border-b border-[#E5E1D8] hover:bg-[#F3EFE7] transition-colors text-start"
                style={{ height: ROW_HEIGHT }}
              >
                <span className="sr-only">{`Add slot at ${h}:00`}</span>
              </button>
            );
          })}
          {/* Overlay: booking blocks */}
          {blocks.map(({ b, from, to }) => {
            const top = (from - START_HOUR) * ROW_HEIGHT;
            const height = (to - from) * ROW_HEIGHT;
            const isRented = b.status === "rented";
            return (
              <button
                key={b.id}
                type="button"
                data-testid={`hourgrid-slot-${b.id}`}
                onClick={(e) => { e.stopPropagation(); onSlotClick(b); }}
                className={`absolute inset-x-1 rounded-md text-white text-[11px] px-2 py-1 shadow-sm border ${isRented ? "bg-[#2A5948] border-[#1F4235] hover:bg-[#1F4235]" : "bg-[#E5B25D] text-[#1C1E17] border-[#B98A2C] hover:bg-[#D9A44E]"}`}
                style={{ top, height, minHeight: 28 }}
              >
                <div className="flex items-start gap-1 leading-tight text-start">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate" dir="ltr">{b.start_time} - {b.end_time}</div>
                    <div className="truncate opacity-90 text-[10px]">
                      {b.guest_name || t("field_bookings_day")} · {money(b.amount)}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
